'use client'

import { useMemo, useState } from 'react'
import { Download, Pencil, Plus, Trash2, Upload } from 'lucide-react'
import ImportPreviewDialog, { type ImportColumn } from './ImportPreviewDialog'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  Button,
  Checkbox,
  Dialog,
  DialogFooter,
  EntityListShell,
  Input,
  Label,
  PageHeader,
  Pagination,
  SearchInput,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from '@rr/ui'
import { downloadBlob, request } from '@/lib/api/client'

async function downloadTemplate(endpointBase: string, filename: string) {
  try {
    await downloadBlob(`${endpointBase}/import/template`, filename)
  } catch (e: any) {
    toast(e?.message ?? 'Failed to download template', 'error')
  }
}

const DEFAULT_PAGE_SIZE = 25

type LocalizedName = { en: string; ru: string; tk: string }

type Currency = {
  id: string
  code: string
  name: string
  nameLocalized: LocalizedName
  symbol: string | null
  precision: number
  isActive: boolean
  isDefault: boolean
}

type VatRate = {
  id: string
  code: string
  name: string
  nameLocalized: LocalizedName
  ratePct: number
  isActive: boolean
  isDefault: boolean
}

type PackingType = {
  id: string
  name: string
  nameLocalized: LocalizedName
  description: string | null
  isActive: boolean
}

type LoadingMethod = {
  id: string
  code: string | null
  name: string
  nameLocalized: LocalizedName
  isActive: boolean
}

type Page<T> = { items: T[]; pagination: { total: number } }

type TabKey = 'currencies' | 'vat' | 'packing' | 'loading'

export default function ReferenceDataPage() {
  const [active, setActive] = useState<TabKey>('currencies')

  return (
    <div className="page-pad">
      <div className="max-w-7xl mx-auto space-y-6">
        <PageHeader
          title="Reference data"
          subtitle="Global taxonomies shared across every tenant."
        />

        <Tabs value={active} onValueChange={(v) => setActive(v as TabKey)}>
          <TabsList className="overflow-x-auto no-scrollbar">
            <TabsTrigger value="currencies">Currencies</TabsTrigger>
            <TabsTrigger value="vat">VAT rates</TabsTrigger>
            <TabsTrigger value="packing">Packing types</TabsTrigger>
            <TabsTrigger value="loading">Loading methods</TabsTrigger>
          </TabsList>

          <TabsContent value="currencies" className="mt-4">
            <CurrenciesTab />
          </TabsContent>
          <TabsContent value="vat" className="mt-4">
            <VatRatesTab />
          </TabsContent>
          <TabsContent value="packing" className="mt-4">
            <PackingTypesTab />
          </TabsContent>
          <TabsContent value="loading" className="mt-4">
            <LoadingMethodsTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}

// ── Currencies (platform-scoped) ─────────────────────────────────────────────

function CurrenciesTab() {
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [editing, setEditing] = useState<Currency | null>(null)
  const [creating, setCreating] = useState(false)
  const [importing, setImporting] = useState(false)

  const filters = useMemo(
    () => ({ page, limit: pageSize, search: search.trim() || undefined }),
    [page, pageSize, search],
  )

  const keyBase = ['platform', 'currencies'] as const
  const listKey = [...keyBase, filters] as const

  const { data, isLoading, isError, error } = useQuery({
    queryKey: listKey,
    queryFn: () => request<Page<Currency>>(`/platform/currencies${toQuery(filters)}`),
    placeholderData: keepPreviousData,
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: keyBase })

  const createMut = useMutation({
    mutationFn: (payload: any) =>
      request<Currency>('/platform/currencies', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => { invalidate(); toast('Currency created', 'success') },
    onError: (e: any) => toast(e?.message ?? 'Create failed', 'error'),
  })
  const updateMut = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) =>
      request<Currency>(`/platform/currencies/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => { invalidate(); toast('Currency updated', 'success') },
    onError: (e: any) => toast(e?.message ?? 'Update failed', 'error'),
  })
  const deleteMut = useMutation({
    mutationFn: (id: string) =>
      request<void>(`/platform/currencies/${id}`, { method: 'DELETE' }),
    onSuccess: () => { invalidate(); toast('Currency removed', 'success') },
    onError: (e: any) => toast(e?.message ?? 'Delete failed', 'error'),
  })

  const items = data?.items ?? []
  const total = data?.pagination?.total ?? items.length

  return (
    <>
      <EntityListShell
        toolbar={
          <>
            <SearchInput
              value={search}
              onChange={(v) => { setSearch(v); setPage(1) }}
              placeholder="Search by code or name…"
              className="max-w-sm"
            />
            <div className="ml-auto flex gap-2">
              <Button
                variant="outline"
                onClick={() => downloadTemplate('/platform/currencies', 'currencies-template.xlsx')}
              >
                <Download className="w-4 h-4" /> Template
              </Button>
              <Button variant="outline" onClick={() => setImporting(true)}>
                <Upload className="w-4 h-4" /> Import
              </Button>
              <Button onClick={() => setCreating(true)}>
                <Plus className="w-4 h-4" /> Add currency
              </Button>
            </div>
          </>
        }
        empty={!isLoading && items.length === 0}
        emptyState={
          isError
            ? error instanceof Error ? error.message : 'Failed to load currencies.'
            : 'No currencies match.'
        }
        pagination={
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            onChange={(p, s) => { setPage(p); setPageSize(s) }}
          />
        }
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-16"><Spinner /></div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="w-20">Symbol</TableHead>
                <TableHead className="w-24">Precision</TableHead>
                <TableHead className="w-24">Active</TableHead>
                <TableHead className="w-24">Default</TableHead>
                <TableHead className="w-28 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="font-mono text-sm">{c.code}</TableCell>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell>{c.symbol ?? '—'}</TableCell>
                  <TableCell className="tnum">{c.precision}</TableCell>
                  <TableCell>{c.isActive ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{c.isDefault ? 'Yes' : '—'}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => setEditing(c)} aria-label={`Edit ${c.code}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (confirm(`Delete ${c.code}?`)) deleteMut.mutate(c.id)
                      }}
                      aria-label={`Delete ${c.code}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </EntityListShell>

      <CurrencyDialog
        open={!!editing || creating}
        item={editing}
        onClose={() => { setEditing(null); setCreating(false) }}
        onSave={(payload) => {
          if (editing) updateMut.mutate({ id: editing.id, payload })
          else createMut.mutate(payload)
          setEditing(null); setCreating(false)
        }}
      />

      <ImportPreviewDialog
        open={importing}
        onClose={() => setImporting(false)}
        title="Import currencies"
        endpointBase="/platform/currencies"
        columns={CURRENCY_IMPORT_COLUMNS}
        onCommitted={() => invalidate()}
        rowLabel={(r: any) => String(r?.code ?? r?.name?.en ?? 'row')}
      />
    </>
  )
}

const CURRENCY_IMPORT_COLUMNS: ImportColumn[] = [
  { path: 'code', label: 'Code', type: 'text', width: 'w-24' },
  { path: 'name.en', label: 'Name (EN)', type: 'text' },
  { path: 'name.ru', label: 'Name (RU)', type: 'text' },
  { path: 'name.tk', label: 'Name (TK)', type: 'text' },
  { path: 'symbol', label: 'Symbol', type: 'text', width: 'w-20' },
  { path: 'precision', label: 'Precision', type: 'number', width: 'w-24' },
  { path: 'isActive', label: 'Active', type: 'bool', width: 'w-20' },
  { path: 'isDefault', label: 'Default', type: 'bool', width: 'w-20' },
]

function CurrencyDialog({
  open, item, onClose, onSave,
}: {
  open: boolean
  item: Currency | null
  onClose: () => void
  onSave: (payload: any) => void
}) {
  const [code, setCode] = useState('')
  const [name, setName] = useState<LocalizedName>({ en: '', ru: '', tk: '' })
  const [symbol, setSymbol] = useState('')
  const [precision, setPrecision] = useState(2)
  const [isActive, setIsActive] = useState(true)
  const [isDefault, setIsDefault] = useState(false)

  const trackId = item?.id ?? '__new__'
  const [hydratedFor, setHydratedFor] = useState<string | null>(null)
  if (open && hydratedFor !== trackId) {
    setCode(item?.code ?? '')
    setName(item?.nameLocalized ?? { en: '', ru: '', tk: '' })
    setSymbol(item?.symbol ?? '')
    setPrecision(item?.precision ?? 2)
    setIsActive(item?.isActive ?? true)
    setIsDefault(item?.isDefault ?? false)
    setHydratedFor(trackId)
  }
  if (!open && hydratedFor !== null) setHydratedFor(null)

  return (
    <Dialog open={open} onClose={onClose} title={item ? `Edit ${item.code}` : 'New currency'} maxWidth="md">
      <form
        id="currency-dialog-form"
        onSubmit={(e) => {
          e.preventDefault()
          onSave({
            code: code.trim(),
            name: { en: name.en.trim(), ru: name.ru.trim(), tk: name.tk.trim() },
            symbol: symbol.trim() || null,
            precision,
            isActive,
            isDefault,
          })
        }}
        className="grid gap-4"
      >
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code (ISO 4217)" htmlFor="cur-code">
            <Input id="cur-code" value={code} onChange={(e) => setCode(e.target.value)} required maxLength={10} />
          </Field>
          <Field label="Symbol" htmlFor="cur-symbol">
            <Input id="cur-symbol" value={symbol} onChange={(e) => setSymbol(e.target.value)} placeholder="$, €" />
          </Field>
        </div>
        <LocalizedNameFields value={name} onChange={setName} idPrefix="cur" />
        <div className="grid grid-cols-3 gap-3 items-end">
          <Field label="Precision" htmlFor="cur-precision">
            <Input
              id="cur-precision"
              type="number"
              min={0}
              max={10}
              value={precision}
              onChange={(e) => setPrecision(Number(e.target.value))}
            />
          </Field>
          <CheckboxRow id="cur-active" label="Active" checked={isActive} onChange={setIsActive} />
          <CheckboxRow id="cur-default" label="Default" checked={isDefault} onChange={setIsDefault} />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" form="currency-dialog-form">{item ? 'Save' : 'Create'}</Button>
        </DialogFooter>
      </form>
    </Dialog>
  )
}

// ── VAT rates (global, mounted under /tenant) ────────────────────────────────

function VatRatesTab() {
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [editing, setEditing] = useState<VatRate | null>(null)
  const [creating, setCreating] = useState(false)
  const [importing, setImporting] = useState(false)

  const filters = useMemo(
    () => ({ page, limit: pageSize, search: search.trim() || undefined }),
    [page, pageSize, search],
  )

  const keyBase = ['tenant', 'vat-rates'] as const
  const listKey = [...keyBase, filters] as const

  const { data, isLoading, isError, error } = useQuery({
    queryKey: listKey,
    queryFn: () => request<Page<VatRate>>(`/tenant/vat-rates${toQuery(filters)}`),
    placeholderData: keepPreviousData,
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: keyBase })

  const createMut = useMutation({
    mutationFn: (payload: any) =>
      request<VatRate>('/tenant/vat-rates', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => { invalidate(); toast('VAT rate created', 'success') },
    onError: (e: any) => toast(e?.message ?? 'Create failed', 'error'),
  })
  const updateMut = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) =>
      request<VatRate>(`/tenant/vat-rates/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => { invalidate(); toast('VAT rate updated', 'success') },
    onError: (e: any) => toast(e?.message ?? 'Update failed', 'error'),
  })
  const deleteMut = useMutation({
    mutationFn: (id: string) =>
      request<void>(`/tenant/vat-rates/${id}`, { method: 'DELETE' }),
    onSuccess: () => { invalidate(); toast('VAT rate removed', 'success') },
    onError: (e: any) => toast(e?.message ?? 'Delete failed', 'error'),
  })

  const items = data?.items ?? []
  const total = data?.pagination?.total ?? items.length

  return (
    <>
      <EntityListShell
        toolbar={
          <>
            <SearchInput
              value={search}
              onChange={(v) => { setSearch(v); setPage(1) }}
              placeholder="Search by code or name…"
              className="max-w-sm"
            />
            <div className="ml-auto flex gap-2">
              <Button
                variant="outline"
                onClick={() => downloadTemplate('/tenant/vat-rates', 'vat-rates-template.xlsx')}
              >
                <Download className="w-4 h-4" /> Template
              </Button>
              <Button variant="outline" onClick={() => setImporting(true)}>
                <Upload className="w-4 h-4" /> Import
              </Button>
              <Button onClick={() => setCreating(true)}>
                <Plus className="w-4 h-4" /> Add VAT rate
              </Button>
            </div>
          </>
        }
        empty={!isLoading && items.length === 0}
        emptyState={
          isError
            ? error instanceof Error ? error.message : 'Failed to load VAT rates.'
            : 'No VAT rates match.'
        }
        pagination={
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            onChange={(p, s) => { setPage(p); setPageSize(s) }}
          />
        }
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-16"><Spinner /></div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-32">Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="w-24">Rate %</TableHead>
                <TableHead className="w-24">Active</TableHead>
                <TableHead className="w-24">Default</TableHead>
                <TableHead className="w-28 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((v) => (
                <TableRow key={v.id}>
                  <TableCell className="font-mono text-sm">{v.code}</TableCell>
                  <TableCell className="font-medium">{v.name}</TableCell>
                  <TableCell className="tnum">{v.ratePct}</TableCell>
                  <TableCell>{v.isActive ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{v.isDefault ? 'Yes' : '—'}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => setEditing(v)} aria-label={`Edit ${v.code}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (confirm(`Delete ${v.code}?`)) deleteMut.mutate(v.id)
                      }}
                      aria-label={`Delete ${v.code}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </EntityListShell>

      <VatRateDialog
        open={!!editing || creating}
        item={editing}
        onClose={() => { setEditing(null); setCreating(false) }}
        onSave={(payload) => {
          if (editing) updateMut.mutate({ id: editing.id, payload })
          else createMut.mutate(payload)
          setEditing(null); setCreating(false)
        }}
      />

      <ImportPreviewDialog
        open={importing}
        onClose={() => setImporting(false)}
        title="Import VAT rates"
        endpointBase="/tenant/vat-rates"
        columns={VAT_IMPORT_COLUMNS}
        onCommitted={() => invalidate()}
        rowLabel={(r: any) => String(r?.code ?? 'row')}
      />
    </>
  )
}

const VAT_IMPORT_COLUMNS: ImportColumn[] = [
  { path: 'code', label: 'Code', type: 'text', width: 'w-28' },
  { path: 'name.en', label: 'Name (EN)', type: 'text' },
  { path: 'name.ru', label: 'Name (RU)', type: 'text' },
  { path: 'name.tk', label: 'Name (TK)', type: 'text' },
  { path: 'ratePct', label: 'Rate %', type: 'number', width: 'w-24' },
  { path: 'isActive', label: 'Active', type: 'bool', width: 'w-20' },
  { path: 'isDefault', label: 'Default', type: 'bool', width: 'w-20' },
]

function VatRateDialog({
  open, item, onClose, onSave,
}: {
  open: boolean
  item: VatRate | null
  onClose: () => void
  onSave: (payload: any) => void
}) {
  const [code, setCode] = useState('')
  const [name, setName] = useState<LocalizedName>({ en: '', ru: '', tk: '' })
  const [ratePct, setRatePct] = useState(0)
  const [isActive, setIsActive] = useState(true)
  const [isDefault, setIsDefault] = useState(false)

  const trackId = item?.id ?? '__new__'
  const [hydratedFor, setHydratedFor] = useState<string | null>(null)
  if (open && hydratedFor !== trackId) {
    setCode(item?.code ?? '')
    setName(item?.nameLocalized ?? { en: '', ru: '', tk: '' })
    setRatePct(item?.ratePct ?? 0)
    setIsActive(item?.isActive ?? true)
    setIsDefault(item?.isDefault ?? false)
    setHydratedFor(trackId)
  }
  if (!open && hydratedFor !== null) setHydratedFor(null)

  return (
    <Dialog open={open} onClose={onClose} title={item ? `Edit ${item.code}` : 'New VAT rate'} maxWidth="md">
      <form
        id="vat-dialog-form"
        onSubmit={(e) => {
          e.preventDefault()
          onSave({
            code: code.trim(),
            name: { en: name.en.trim(), ru: name.ru.trim(), tk: name.tk.trim() },
            ratePct,
            isActive,
            isDefault,
          })
        }}
        className="grid gap-4"
      >
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code" htmlFor="vat-code">
            <Input id="vat-code" value={code} onChange={(e) => setCode(e.target.value)} required maxLength={50} />
          </Field>
          <Field label="Rate %" htmlFor="vat-rate">
            <Input
              id="vat-rate"
              type="number"
              min={0}
              max={100}
              step="0.01"
              value={ratePct}
              onChange={(e) => setRatePct(Number(e.target.value))}
              required
            />
          </Field>
        </div>
        <LocalizedNameFields value={name} onChange={setName} idPrefix="vat" />
        <div className="grid grid-cols-2 gap-3 items-end">
          <CheckboxRow id="vat-active" label="Active" checked={isActive} onChange={setIsActive} />
          <CheckboxRow id="vat-default" label="Default" checked={isDefault} onChange={setIsDefault} />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" form="vat-dialog-form">{item ? 'Save' : 'Create'}</Button>
        </DialogFooter>
      </form>
    </Dialog>
  )
}

// ── Packing types (global, platform-scoped) ──────────────────────────────────

function PackingTypesTab() {
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [editing, setEditing] = useState<PackingType | null>(null)
  const [creating, setCreating] = useState(false)
  const [importing, setImporting] = useState(false)

  const filters = useMemo(
    () => ({ page, limit: pageSize, search: search.trim() || undefined }),
    [page, pageSize, search],
  )

  const keyBase = ['platform', 'packing-types'] as const
  const listKey = [...keyBase, filters] as const

  const { data, isLoading, isError, error } = useQuery({
    queryKey: listKey,
    queryFn: () =>
      request<Page<PackingType>>(`/platform/packing-types${toQuery(filters)}`),
    placeholderData: keepPreviousData,
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: keyBase })

  const createMut = useMutation({
    mutationFn: (payload: any) =>
      request<PackingType>('/platform/packing-types', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => { invalidate(); toast('Packing type created', 'success') },
    onError: (e: any) => toast(e?.message ?? 'Create failed', 'error'),
  })
  const updateMut = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) =>
      request<PackingType>(`/platform/packing-types/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => { invalidate(); toast('Packing type updated', 'success') },
    onError: (e: any) => toast(e?.message ?? 'Update failed', 'error'),
  })
  const deleteMut = useMutation({
    mutationFn: (id: string) =>
      request<void>(`/platform/packing-types/${id}`, { method: 'DELETE' }),
    onSuccess: () => { invalidate(); toast('Packing type removed', 'success') },
    onError: (e: any) => toast(e?.message ?? 'Delete failed', 'error'),
  })

  const items = data?.items ?? []
  const total = data?.pagination?.total ?? items.length

  return (
    <>
      <EntityListShell
        toolbar={
          <>
            <SearchInput
              value={search}
              onChange={(v) => { setSearch(v); setPage(1) }}
              placeholder="Search by name or description…"
              className="max-w-sm"
            />
            <div className="ml-auto flex gap-2">
              <Button
                variant="outline"
                onClick={() => downloadTemplate('/platform/packing-types', 'packing-types-template.xlsx')}
              >
                <Download className="w-4 h-4" /> Template
              </Button>
              <Button variant="outline" onClick={() => setImporting(true)}>
                <Upload className="w-4 h-4" /> Import
              </Button>
              <Button onClick={() => setCreating(true)}>
                <Plus className="w-4 h-4" /> Add packing type
              </Button>
            </div>
          </>
        }
        empty={!isLoading && items.length === 0}
        emptyState={
          isError
            ? error instanceof Error ? error.message : 'Failed to load packing types.'
            : 'No packing types match.'
        }
        pagination={
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            onChange={(p, s) => { setPage(p); setPageSize(s) }}
          />
        }
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-16"><Spinner /></div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-24">Active</TableHead>
                <TableHead className="w-28 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{p.description ?? '—'}</TableCell>
                  <TableCell>{p.isActive ? 'Yes' : 'No'}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => setEditing(p)} aria-label={`Edit ${p.name}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (confirm(`Delete "${p.name}"?`)) deleteMut.mutate(p.id)
                      }}
                      aria-label={`Delete ${p.name}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </EntityListShell>

      <PackingTypeDialog
        open={!!editing || creating}
        item={editing}
        onClose={() => { setEditing(null); setCreating(false) }}
        onSave={(payload) => {
          if (editing) updateMut.mutate({ id: editing.id, payload })
          else createMut.mutate(payload)
          setEditing(null); setCreating(false)
        }}
      />

      <ImportPreviewDialog
        open={importing}
        onClose={() => setImporting(false)}
        title="Import packing types"
        endpointBase="/platform/packing-types"
        columns={PACKING_IMPORT_COLUMNS}
        onCommitted={() => invalidate()}
        rowLabel={(r: any) => String(r?.name?.en ?? 'row')}
      />
    </>
  )
}

const PACKING_IMPORT_COLUMNS: ImportColumn[] = [
  { path: 'name.en', label: 'Name (EN)', type: 'text' },
  { path: 'name.ru', label: 'Name (RU)', type: 'text' },
  { path: 'name.tk', label: 'Name (TK)', type: 'text' },
  { path: 'description', label: 'Description', type: 'text' },
  { path: 'isActive', label: 'Active', type: 'bool', width: 'w-20' },
]

function PackingTypeDialog({
  open, item, onClose, onSave,
}: {
  open: boolean
  item: PackingType | null
  onClose: () => void
  onSave: (payload: any) => void
}) {
  const [name, setName] = useState<LocalizedName>({ en: '', ru: '', tk: '' })
  const [description, setDescription] = useState('')
  const [isActive, setIsActive] = useState(true)

  const trackId = item?.id ?? '__new__'
  const [hydratedFor, setHydratedFor] = useState<string | null>(null)
  if (open && hydratedFor !== trackId) {
    setName(item?.nameLocalized ?? { en: '', ru: '', tk: '' })
    setDescription(item?.description ?? '')
    setIsActive(item?.isActive ?? true)
    setHydratedFor(trackId)
  }
  if (!open && hydratedFor !== null) setHydratedFor(null)

  return (
    <Dialog open={open} onClose={onClose} title={item ? `Edit ${item.name}` : 'New packing type'} maxWidth="md">
      <form
        id="packing-dialog-form"
        onSubmit={(e) => {
          e.preventDefault()
          onSave({
            name: { en: name.en.trim(), ru: name.ru.trim(), tk: name.tk.trim() },
            description: description.trim() || null,
            isActive,
          })
        }}
        className="grid gap-4"
      >
        <LocalizedNameFields value={name} onChange={setName} idPrefix="pack" />
        <Field label="Description" htmlFor="pack-desc">
          <Input
            id="pack-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. 800×1200×145mm"
          />
        </Field>
        <CheckboxRow id="pack-active" label="Active" checked={isActive} onChange={setIsActive} />
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" form="packing-dialog-form">{item ? 'Save' : 'Create'}</Button>
        </DialogFooter>
      </form>
    </Dialog>
  )
}

// ── Loading methods (global, platform-scoped) ────────────────────────────────

function LoadingMethodsTab() {
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [editing, setEditing] = useState<LoadingMethod | null>(null)
  const [creating, setCreating] = useState(false)
  const [importing, setImporting] = useState(false)

  const filters = useMemo(
    () => ({ page, limit: pageSize, search: search.trim() || undefined }),
    [page, pageSize, search],
  )

  const keyBase = ['platform', 'loading-methods'] as const
  const listKey = [...keyBase, filters] as const

  const { data, isLoading, isError, error } = useQuery({
    queryKey: listKey,
    queryFn: () =>
      request<Page<LoadingMethod>>(`/platform/loading-methods${toQuery(filters)}`),
    placeholderData: keepPreviousData,
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: keyBase })

  const createMut = useMutation({
    mutationFn: (payload: any) =>
      request<LoadingMethod>('/platform/loading-methods', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => { invalidate(); toast('Loading method created', 'success') },
    onError: (e: any) => toast(e?.message ?? 'Create failed', 'error'),
  })
  const updateMut = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) =>
      request<LoadingMethod>(`/platform/loading-methods/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => { invalidate(); toast('Loading method updated', 'success') },
    onError: (e: any) => toast(e?.message ?? 'Update failed', 'error'),
  })
  const deleteMut = useMutation({
    mutationFn: (id: string) =>
      request<void>(`/platform/loading-methods/${id}`, { method: 'DELETE' }),
    onSuccess: () => { invalidate(); toast('Loading method removed', 'success') },
    onError: (e: any) => toast(e?.message ?? 'Delete failed', 'error'),
  })

  const items = data?.items ?? []
  const total = data?.pagination?.total ?? items.length

  return (
    <>
      <EntityListShell
        toolbar={
          <>
            <SearchInput
              value={search}
              onChange={(v) => { setSearch(v); setPage(1) }}
              placeholder="Search by code or name…"
              className="max-w-sm"
            />
            <div className="ml-auto flex gap-2">
              <Button
                variant="outline"
                onClick={() => downloadTemplate('/platform/loading-methods', 'loading-methods-template.xlsx')}
              >
                <Download className="w-4 h-4" /> Template
              </Button>
              <Button variant="outline" onClick={() => setImporting(true)}>
                <Upload className="w-4 h-4" /> Import
              </Button>
              <Button onClick={() => setCreating(true)}>
                <Plus className="w-4 h-4" /> Add loading method
              </Button>
            </div>
          </>
        }
        empty={!isLoading && items.length === 0}
        emptyState={
          isError
            ? error instanceof Error ? error.message : 'Failed to load loading methods.'
            : 'No loading methods match.'
        }
        pagination={
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            onChange={(p, s) => { setPage(p); setPageSize(s) }}
          />
        }
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-16"><Spinner /></div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-28">Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="w-24">Active</TableHead>
                <TableHead className="w-28 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((l) => (
                <TableRow key={l.id}>
                  <TableCell className="font-mono text-sm">{l.code ?? '—'}</TableCell>
                  <TableCell className="font-medium">{l.name}</TableCell>
                  <TableCell>{l.isActive ? 'Yes' : 'No'}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => setEditing(l)} aria-label={`Edit ${l.name}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (confirm(`Delete "${l.name}"?`)) deleteMut.mutate(l.id)
                      }}
                      aria-label={`Delete ${l.name}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </EntityListShell>

      <LoadingMethodDialog
        open={!!editing || creating}
        item={editing}
        onClose={() => { setEditing(null); setCreating(false) }}
        onSave={(payload) => {
          if (editing) updateMut.mutate({ id: editing.id, payload })
          else createMut.mutate(payload)
          setEditing(null); setCreating(false)
        }}
      />

      <ImportPreviewDialog
        open={importing}
        onClose={() => setImporting(false)}
        title="Import loading methods"
        endpointBase="/platform/loading-methods"
        columns={LOADING_IMPORT_COLUMNS}
        onCommitted={() => invalidate()}
        rowLabel={(r: any) => String(r?.code ?? r?.name?.en ?? 'row')}
      />
    </>
  )
}

const LOADING_IMPORT_COLUMNS: ImportColumn[] = [
  { path: 'code', label: 'Code', type: 'text', width: 'w-28' },
  { path: 'name.en', label: 'Name (EN)', type: 'text' },
  { path: 'name.ru', label: 'Name (RU)', type: 'text' },
  { path: 'name.tk', label: 'Name (TK)', type: 'text' },
  { path: 'isActive', label: 'Active', type: 'bool', width: 'w-20' },
]

function LoadingMethodDialog({
  open, item, onClose, onSave,
}: {
  open: boolean
  item: LoadingMethod | null
  onClose: () => void
  onSave: (payload: any) => void
}) {
  const [code, setCode] = useState('')
  const [name, setName] = useState<LocalizedName>({ en: '', ru: '', tk: '' })
  const [isActive, setIsActive] = useState(true)

  const trackId = item?.id ?? '__new__'
  const [hydratedFor, setHydratedFor] = useState<string | null>(null)
  if (open && hydratedFor !== trackId) {
    setCode(item?.code ?? '')
    setName(item?.nameLocalized ?? { en: '', ru: '', tk: '' })
    setIsActive(item?.isActive ?? true)
    setHydratedFor(trackId)
  }
  if (!open && hydratedFor !== null) setHydratedFor(null)

  return (
    <Dialog open={open} onClose={onClose} title={item ? `Edit ${item.name}` : 'New loading method'} maxWidth="md">
      <form
        id="loading-dialog-form"
        onSubmit={(e) => {
          e.preventDefault()
          onSave({
            code: code.trim() || null,
            name: { en: name.en.trim(), ru: name.ru.trim(), tk: name.tk.trim() },
            isActive,
          })
        }}
        className="grid gap-4"
      >
        <Field label="Code" htmlFor="load-code">
          <Input
            id="load-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="SIDE, TOP, REAR"
            maxLength={50}
          />
        </Field>
        <LocalizedNameFields value={name} onChange={setName} idPrefix="load" />
        <CheckboxRow id="load-active" label="Active" checked={isActive} onChange={setIsActive} />
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" form="loading-dialog-form">{item ? 'Save' : 'Create'}</Button>
        </DialogFooter>
      </form>
    </Dialog>
  )
}

// ── Shared form bits ─────────────────────────────────────────────────────────

function Field({ label, htmlFor, children }: { label: string; htmlFor: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
    </div>
  )
}

function LocalizedNameFields({
  value, onChange, idPrefix,
}: {
  value: LocalizedName
  onChange: (next: LocalizedName) => void
  idPrefix: string
}) {
  return (
    <div className="grid grid-cols-3 gap-3">
      <Field label="Name (EN)" htmlFor={`${idPrefix}-name-en`}>
        <Input
          id={`${idPrefix}-name-en`}
          value={value.en}
          onChange={(e) => onChange({ ...value, en: e.target.value })}
          required
        />
      </Field>
      <Field label="Name (RU)" htmlFor={`${idPrefix}-name-ru`}>
        <Input
          id={`${idPrefix}-name-ru`}
          value={value.ru}
          onChange={(e) => onChange({ ...value, ru: e.target.value })}
          required
        />
      </Field>
      <Field label="Name (TK)" htmlFor={`${idPrefix}-name-tk`}>
        <Input
          id={`${idPrefix}-name-tk`}
          value={value.tk}
          onChange={(e) => onChange({ ...value, tk: e.target.value })}
          required
        />
      </Field>
    </div>
  )
}

function CheckboxRow({
  id, label, checked, onChange,
}: { id: string; label: string; checked: boolean; onChange: (next: boolean) => void }) {
  return (
    <label htmlFor={id} className="flex items-center gap-2 text-sm select-none">
      <Checkbox id={id} checked={checked} onCheckedChange={(v) => onChange(Boolean(v))} />
      {label}
    </label>
  )
}

function toQuery(params: Record<string, unknown>): string {
  const sp = new URLSearchParams()
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return
    sp.set(k, String(v))
  })
  const s = sp.toString()
  return s ? `?${s}` : ''
}
